import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    fetchMaintenanceDocs,
    fetchMaintenanceDocById,
    fetchMaintenanceDocsByMaintenanceId,
    uploadMaintenanceDoc,
    updateMaintenanceDoc,
    deleteMaintenanceDoc,
} from "../../api/maintenance/maintenanceDocService";

export const useMaintenanceDocs = (manId) => {
    const queryClient = useQueryClient();

    const queryKey = ["maintenanceDocs", manId];
    const queryFn = () => fetchMaintenanceDocsByMaintenanceId(manId);

    const {
        data: maintenanceDocs = [],
        error,
        isLoading,
        isFetching,
    } = useQuery({
        queryKey: queryKey,
        queryFn: queryFn,
        enabled: !!manId,
        staleTime: 1000 * 60 * 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    const invalidateMaintenanceDocsCache = () => {
        queryClient.invalidateQueries({ queryKey: ["maintenanceDocs", manId] });
    };
    
    const createMutation = useMutation({
        mutationFn: uploadMaintenanceDoc,
        onSuccess: (newDocData) => {
            invalidateMaintenanceDocsCache();
            queryClient.setQueryData(["maintenanceDoc", newDocData.docid], newDocData);
            queryClient.invalidateQueries({ queryKey: ["maintenanceHistory", newDocData.manid] });
        },
    });

    const updateMutation = useMutation({
        mutationFn: updateMaintenanceDoc,
        onSuccess: (updatedDocData) => {
            invalidateMaintenanceDocsCache();
            queryClient.setQueryData(["maintenanceDoc", updatedDocData.docid], updatedDocData);
            queryClient.invalidateQueries({ queryKey: ["maintenanceHistory", updatedDocData.manid] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteMaintenanceDoc,
        onSuccess: (_, variables) => { 
            invalidateMaintenanceDocsCache();
            const docIdToDelete = variables.id;
            const docDataInCache = queryClient.getQueryData(["maintenanceDoc", docIdToDelete]);
            if (docDataInCache && docDataInCache.manid) {
                queryClient.invalidateQueries({ queryKey: ["maintenanceHistory", docDataInCache.manid] });
            } else {
                queryClient.invalidateQueries({ queryKey: ["maintenanceHistory", manId] });
            } 
        }
    });

    return {
        maintenanceDocs,
        isLoading,
        isFetching,
        error,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};

export const useMaintenanceDocById = (docId) => {
    return useQuery({
        queryKey: ["maintenanceDoc", docId],
        queryFn: () => fetchMaintenanceDocById(docId),
        enabled: !!docId,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};